import {
  SurveyEntity as BaseSurveyEntity,
  SurveyType,
} from "@graffer-inc/cloud-gov-core";
import {
  FormSchema,
  SectionElement,
  StringGenerator,
  isStaticElement,
} from "@graffer-inc/form-schema";
import { PORTAL_BASE_URL } from "../../../utils/url";
import { PermissionSet, GroupPermissionAttached } from "../rbac/permission-set";
import { SurveysPermissions } from "../rbac/permissions";
import envConfig from "../../../utils/env-config";

export const customUrlAllowedCharRegex = /^[a-zA-Z0-9-]+$/;
export const surveyIdFormatRegex = /^[0-9]{19}$/;

type PublishableStatus = {
  publishable: boolean;
  errorMessage: string;
};

export class SurveyEntity
  extends BaseSurveyEntity
  implements GroupPermissionAttached
{
  static newFormSchema() {
    return FormSchema.fromJSON({
      version: "1.0.0",
      root: [
        SectionElement.create({
          id: StringGenerator.generate(),
          title: "アンケート",
          children: [],
        }),
      ],
    });
  }

  resolvePermission(groupId: string): PermissionSet {
    if (this.groupIds && this.groupIds.includes(groupId)) {
      // 現時点では、resource <> group間での権限の差をつける機能は提供していないため、
      // アンケートに関する権限を全て権限を付ける
      return new PermissionSet(SurveysPermissions);
    }
    return new PermissionSet([]);
  }

  validatePublishable(): PublishableStatus {
    if (!this.draft.formSchema || !this.draft.formSchema.root) {
      return {
        publishable: false,
        errorMessage: "フォーム設定が定義されていません",
      };
    }

    // 入力要素が存在しないsectionを取得
    const indexOfSectionWithoutInputElement =
      this.draft.formSchema.root.findIndex(
        (s) => !s.children.some((elem) => !isStaticElement(elem)),
      );
    if (indexOfSectionWithoutInputElement !== -1) {
      return {
        publishable: false,
        errorMessage: `フォーム設定の${indexOfSectionWithoutInputElement + 1}ページに入力要素が設定されていません`,
      };
    }

    return {
      publishable: true,
      errorMessage: "",
    };
  }

  isUnPublishable() {
    return !["DRAFT", "UNPUBLISHED"].includes(this.publishStatus);
  }

  isPublished() {
    return !!this.published;
  }

  copy() {
    return new SurveyEntity(this);
  }

  private surveyPath(orgSlug: string): string {
    return `${orgSlug}/smart-apply/surveys/${this.id}`;
  }

  private surveyCustomPath(orgSlug: string): string {
    return `${orgSlug}/smart-apply/surveys-alias/${this.customPath}`;
  }

  answerSurveyURL(orgSlug: string): string {
    return !!this.customPath
      ? `${PORTAL_BASE_URL}/${this.surveyCustomPath(orgSlug)}`
      : `${PORTAL_BASE_URL}/${this.surveyPath(orgSlug)}`;
  }

  answerSurveyLGWANURL(orgSlug: string): string {
    return !!this.customPath
      ? `${envConfig.getLgwanHost()}/${this.surveyCustomPath(orgSlug)}`
      : `${envConfig.getLgwanHost()}/${this.surveyPath(orgSlug)}`;
  }

  previewURL(orgSlug: string, previewToken: string, isLGWAN: boolean): string {
    return isLGWAN
      ? `${envConfig.getLgwanHost()}/${this.surveyPath(orgSlug)}?surveyPreviewToken=${previewToken}`
      : `${PORTAL_BASE_URL}/${this.surveyPath(orgSlug)}?surveyPreviewToken=${previewToken}`;
  }

  static validateName(name: string): string | undefined {
    if (name.length < 1 || name.length > 64) {
      return "1文字以上64文字以内で入力してください";
    }
    return undefined;
  }

  static validatePath(customPath: string | undefined): string | undefined {
    if (!customPath) {
      return undefined;
    }
    if (
      customPath.length === 0 ||
      !customPath.match(customUrlAllowedCharRegex)
    ) {
      return "半角英数字、ハイフン(-)で入力してください";
    }
    if (customPath.match(surveyIdFormatRegex)) {
      return "手続きIDと同じ19桁数字の形式は使用できません";
    }
    if (customPath.length > 64) {
      return "64文字以内で入力してください";
    }
    return undefined;
  }

  static fromJSON(values: SurveyType) {
    return new SurveyEntity(values);
  }

  static fromJSONArray(json?: SurveyType[]) {
    if (!json) {
      return [];
    }
    return json.map(SurveyEntity.fromJSON);
  }
}
